import { ExternalLink } from "@/components/ui/icons";
import { useId } from "react";
import { Link } from "react-aria-components/Link";
import { Text, View } from "react-native";

import { PROVIDER_LOCKUPS } from "@/components/brand/provider-logos";
import { ProviderLockup } from "@/components/market/PriceStack";

import { MarketplaceSearchFeedback } from "@/components/market/MarketplaceSearchFeedback";
import type { Condition } from "@/hooks/useApi";
import { useMarketplaceSearch } from "@/hooks/useMarketplaceSearch";
import { EBAY_SIGN_IN_HINT, ebayEdition, ebaySoldsForwardPath } from "@/lib/scan/ebaySolds";
import { useThemeColors } from "@/theme/theme";

/**
 * A user-initiated eBay sold-items search for one card (`ebaySoldsUrl`), shared by Scan, the
 * vendor's card detail and the public card pages, so a dealer and a customer check the same sold
 * comps the same way.
 *
 * `testID` names it for a surface's harness rungs. `track` reports the search to the vendor app's
 * analytics; a public page passes `false`, because its visitors are not the app's users and its own
 * analytics follow ADR-0061.
 */
export function EbaySoldsLink(
  { url, name, testID, graded, condition, printing, track = true, inline = false }: {
    url: string;
    name: string;
    testID: string;
    graded: boolean;
    condition?: Condition | null;
    printing?: string | null;
    track?: boolean;
    /** A bare link for a card that already frames it, such as the desktop sales table. */
    inline?: boolean;
  },
) {
  const c = useThemeColors();
  const hintId = useId();
  const linkProps = {
    // Through a same-site forward, so iPhone Safari does not hand the search to the eBay app,
    // which drops the sold filters (`ebaySoldsForwardPath`).
    href: ebaySoldsForwardPath(url),
    target: "_blank",
    rel: "noopener noreferrer",
  };
  const feedback = useMarketplaceSearch({
    marketplace: "ebay",
    url,
    track,
    properties: {
      search_query: new URLSearchParams(url.split("?")[1]).get("_nkw") ?? undefined,
      card_state: graded ? "graded" : "raw",
      edition: ebayEdition(name, printing),
      condition: graded ? undefined : condition ?? undefined,
      platform: "web",
      surface: testID.startsWith("browse-")
        ? "public_card"
        : testID.endsWith("card-detail")
        ? "card_detail"
        : "scan",
    },
  });
  return (
    <View>
      <Link
        {...linkProps}
        onPress={() => {
          feedback.start();
        }}
        data-testid={testID}
        aria-label={`Search eBay sold items for ${name}`}
        aria-describedby={hintId}
        className={inline
          ? "flex no-underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary min-h-9 flex-row items-center gap-2 active:opacity-70"
          : "flex no-underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary min-h-12 flex-row items-center gap-3 rounded-lg border border-border bg-surface px-3 active:bg-surface-muted"}
      >
        <View className={inline ? "items-start gap-0.5" : "flex-1 gap-0.5 py-2"}>
          <View className="flex-row items-center gap-1.5">
            <Text className="font-label text-[13px] text-primary">Find it on</Text>
            <ProviderLockup logo={PROVIDER_LOCKUPS.ebay} height={18} label="eBay" />
          </View>
          <Text nativeID={hintId} className="text-xs text-muted-foreground">
            {EBAY_SIGN_IN_HINT}
          </Text>
        </View>
        <ExternalLink aria-hidden size={14} color={c.primary} />
      </Link>
      {feedback.pending
        ? <MarketplaceSearchFeedback marketplace="eBay" onRespond={feedback.respond} />
        : null}
    </View>
  );
}
